import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  ViewChild,
  computed,
  signal,
  viewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AudioPlayerModule } from '@app-components/audio-player/audio-player.module';
import { NgxGalleryModule } from '@app-components/ngx-gallery-video/ngx-gallery.module';
import { StringUtils } from '@app-utils/StringUtils';
import { NgxGalleryComponent, NgxGalleryImage, NgxGalleryOptions } from 'ngx-gallery-images-video';

export type MediaTab = 'images' | 'video' | 'audio';

export interface ReportItemAudioData {
  url: string;
  duration?: number;
}

export interface DialogViewReportItemMediaData {
  title: string;
  photosAndVideosUrls?: string[];
  audio?: ReportItemAudioData | null;
  initialTab?: MediaTab;
}

@Component({
  selector: 'app-dialog-view-report-item-media',
  standalone: true,
  imports: [CommonModule, MatDialogModule, AudioPlayerModule, NgxGalleryModule],
  templateUrl: './dialog-view-report-item-media.component.html',
  styleUrls: ['./dialog-view-report-item-media.component.scss'],
})
export class DialogViewReportItemMediaComponent implements AfterViewInit, OnDestroy {
  @ViewChild('previewGallery') previewGallery!: NgxGalleryComponent;

  private readonly videoPlayer = viewChild<ElementRef<HTMLVideoElement>>('videoPlayer');
  private readonly imageThumbsTrack = viewChild<ElementRef<HTMLElement>>('imageThumbsTrack');
  private readonly videoThumbsTrack = viewChild<ElementRef<HTMLElement>>('videoThumbsTrack');

  private imageResizeObserver: ResizeObserver | null = null;
  private videoResizeObserver: ResizeObserver | null = null;

  readonly title: string;
  readonly images = signal<string[]>([]);
  readonly videos = signal<string[]>([]);
  readonly audio = signal<ReportItemAudioData | null>(null);

  readonly activeTab = signal<MediaTab>('images');
  readonly selectedImageIndex = signal(0);
  readonly selectedVideoIndex = signal(0);
  readonly isVideoPlaying = signal(false);

  readonly canScrollImagesPrev = signal(false);
  readonly canScrollImagesNext = signal(false);
  readonly canScrollVideosPrev = signal(false);
  readonly canScrollVideosNext = signal(false);

  galleryOptions: NgxGalleryOptions[] = [];
  galleryImages: NgxGalleryImage[] = [];

  readonly imageCount = computed(() => this.images().length);
  readonly videoCount = computed(() => this.videos().length);
  readonly audioCount = computed(() => (this.audio() ? 1 : 0));

  readonly showImageArrows = computed(
    () => this.canScrollImagesPrev() || this.canScrollImagesNext()
  );
  readonly showVideoArrows = computed(
    () => this.canScrollVideosPrev() || this.canScrollVideosNext()
  );
  readonly canNavigateImagesPrev = computed(() => this.selectedImageIndex() > 0);
  readonly canNavigateImagesNext = computed(
    () => this.selectedImageIndex() < this.imageCount() - 1
  );

  readonly selectedImageSrc = computed(() => {
    const list = this.images();
    const index = this.selectedImageIndex();
    return list[index] ? StringUtils.getMediaSrc(list[index]) : null;
  });

  readonly selectedVideoSrc = computed(() => {
    const list = this.videos();
    const index = this.selectedVideoIndex();
    return list[index] ? StringUtils.getMediaSrc(list[index]) : null;
  });

  constructor(
    private readonly dialogRef: MatDialogRef<DialogViewReportItemMediaComponent>,
    @Inject(MAT_DIALOG_DATA) data: DialogViewReportItemMediaData
  ) {
    this.title = data.title;

    const urls = data.photosAndVideosUrls ?? [];
    const imageUrls = urls.filter(url => !StringUtils.isVideoUrl(url));
    this.images.set(imageUrls);
    this.videos.set(urls.filter(url => StringUtils.isVideoUrl(url)));
    this.audio.set(data.audio ?? null);

    this.galleryOptions = [
      {
        image: false,
        thumbnails: false,
        width: '0px',
        height: '0px',
        previewZoom: true,
        previewRotate: true,
        previewCloseOnEsc: true,
        previewCloseOnClick: true,
        previewKeyboardNavigation: true,
      },
    ];

    this.galleryImages = imageUrls.map(url => {
      const src = StringUtils.getMediaSrc(url);
      return { small: src, medium: src, big: src };
    });

    this.activeTab.set(data.initialTab ?? this.resolveInitialTab());
  }

  ngAfterViewInit(): void {
    this.setupThumbsObservers();
    queueMicrotask(() => this.refreshThumbsScrollState());
  }

  ngOnDestroy(): void {
    this.imageResizeObserver?.disconnect();
    this.videoResizeObserver?.disconnect();
  }

  @HostListener('window:keyup.esc')
  close(): void {
    if (this.previewGallery?.previewEnabled) return;
    this.dialogRef.close();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.refreshThumbsScrollState();
  }

  selectTab(tab: MediaTab): void {
    if (this.activeTab() === tab) return;
    this.pauseVideo();
    this.activeTab.set(tab);
    queueMicrotask(() => {
      this.setupThumbsObservers();
      this.refreshThumbsScrollState();
    });
  }

  selectImage(index: number): void {
    this.selectedImageIndex.set(index);
    this.scrollThumbIntoView('images', index);
  }

  navigateImage(direction: -1 | 1, event?: Event): void {
    event?.stopPropagation();
    const nextIndex = this.selectedImageIndex() + direction;
    if (nextIndex < 0 || nextIndex >= this.imageCount()) return;
    this.selectImage(nextIndex);
  }

  openImagePreview(index: number = this.selectedImageIndex()): void {
    if (!this.previewGallery || !this.galleryImages.length) return;
    this.previewGallery.openPreview(index);
  }

  selectVideo(index: number): void {
    this.pauseVideo();
    this.selectedVideoIndex.set(index);
    this.isVideoPlaying.set(false);
    this.scrollThumbIntoView('video', index);
  }

  scrollImages(direction: -1 | 1): void {
    this.scrollThumbsTrack(this.imageThumbsTrack()?.nativeElement, direction, 'images');
  }

  scrollVideos(direction: -1 | 1): void {
    this.scrollThumbsTrack(this.videoThumbsTrack()?.nativeElement, direction, 'video');
  }

  onImagesScroll(): void {
    this.updateScrollState(this.imageThumbsTrack()?.nativeElement, 'images');
  }

  onVideosScroll(): void {
    this.updateScrollState(this.videoThumbsTrack()?.nativeElement, 'video');
  }

  playVideo(): void {
    const video = this.videoPlayer()?.nativeElement;
    if (!video) return;
    video.play().then(() => this.isVideoPlaying.set(true)).catch(() => {});
  }

  onVideoPlay(): void {
    this.isVideoPlaying.set(true);
  }

  onVideoPause(): void {
    this.isVideoPlaying.set(false);
  }

  getMediaSrc(url: string): string {
    return StringUtils.getMediaSrc(url);
  }

  private pauseVideo(): void {
    const video = this.videoPlayer()?.nativeElement;
    if (video && !video.paused) {
      video.pause();
    }
    this.isVideoPlaying.set(false);
  }

  private resolveInitialTab(): MediaTab {
    if (this.imageCount() > 0) return 'images';
    if (this.videoCount() > 0) return 'video';
    if (this.audioCount() > 0) return 'audio';
    return 'images';
  }

  private setupThumbsObservers(): void {
    this.imageResizeObserver?.disconnect();
    this.videoResizeObserver?.disconnect();

    const imageTrack = this.imageThumbsTrack()?.nativeElement;
    if (imageTrack && typeof ResizeObserver !== 'undefined') {
      this.imageResizeObserver = new ResizeObserver(() => this.updateScrollState(imageTrack, 'images'));
      this.imageResizeObserver.observe(imageTrack);
    }

    const videoTrack = this.videoThumbsTrack()?.nativeElement;
    if (videoTrack && typeof ResizeObserver !== 'undefined') {
      this.videoResizeObserver = new ResizeObserver(() => this.updateScrollState(videoTrack, 'video'));
      this.videoResizeObserver.observe(videoTrack);
    }
  }

  private refreshThumbsScrollState(): void {
    this.updateScrollState(this.imageThumbsTrack()?.nativeElement, 'images');
    this.updateScrollState(this.videoThumbsTrack()?.nativeElement, 'video');
  }

  private scrollThumbsTrack(
    track: HTMLElement | undefined,
    direction: -1 | 1,
    type: 'images' | 'video'
  ): void {
    if (!track) return;
    const step = Math.max(track.clientWidth * 0.75, 120);
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
    setTimeout(() => this.updateScrollState(track, type), 320);
  }

  private scrollThumbIntoView(type: 'images' | 'video', index: number): void {
    const track =
      type === 'images'
        ? this.imageThumbsTrack()?.nativeElement
        : this.videoThumbsTrack()?.nativeElement;
    if (!track) return;

    const thumb = track.children.item(index) as HTMLElement | null;
    thumb?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    setTimeout(() => this.updateScrollState(track, type), 320);
  }

  private updateScrollState(track: HTMLElement | undefined, type: 'images' | 'video'): void {
    if (!track) {
      if (type === 'images') {
        this.canScrollImagesPrev.set(false);
        this.canScrollImagesNext.set(false);
      } else {
        this.canScrollVideosPrev.set(false);
        this.canScrollVideosNext.set(false);
      }
      return;
    }

    const maxScroll = track.scrollWidth - track.clientWidth;
    const hasOverflow = maxScroll > 2;
    const canPrev = hasOverflow && track.scrollLeft > 2;
    const canNext = hasOverflow && track.scrollLeft < maxScroll - 2;

    if (type === 'images') {
      this.canScrollImagesPrev.set(canPrev);
      this.canScrollImagesNext.set(canNext);
    } else {
      this.canScrollVideosPrev.set(canPrev);
      this.canScrollVideosNext.set(canNext);
    }
  }
}
